import useFileOperations from 'hooks/useFileOperations';
import { useParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { CREATE_CONVERSATION_AUTH } from 'schema-v2/Chat/mutations';
import { Channel, ChannelFilters, LocalUploadAttachment, StreamChat } from 'stream-chat';
import { Chat, useChatContext, useCreateChatClient } from 'stream-chat-react';
import {
  ACCEPTED_FILE_TYPES_IN_DMS,
  CHANNEL_TYPE,
  MAX_FILES_ALLOWED_IN_DMS,
  MAX_FILE_SIZE_MB_IN_DMS,
  UNIFIED_GRAPH,
} from 'utils/constants';
import { conversationFilter } from 'utils/new/conversationFilter';
import { failureToastMessage } from 'utils/toastHelpers';

import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';

import { useCommunityContext } from 'components/CommunityContext';
import PageSkeleton from 'components/PageSkeleton';

const StreamContextMapper = ({
  children,
}: {
  children: ({
    session,
    setActiveConversation,
    currentConversation,
    hasUnreadMessages,
    baseChannelFilters,
    conversations,
  }: {
    session: StreamChat;
    setActiveConversation: (conversation: Channel) => void;
    currentConversation: Channel | null;
    hasUnreadMessages: boolean;
    baseChannelFilters: ChannelFilters;
    conversations: Channel[];
  }) => ReactNode;
}) => {
  const { client, setActiveChannel } = useChatContext();
  const { conversationId } = useParams();
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [conversations, setConversations] = useState<Channel[]>([]);

  const currentConversation = useMemo(() => {
    if (!conversationId) return null;
    return client?.getChannelById(CHANNEL_TYPE, conversationId as string, {});
  }, [client, conversationId]);

  useEffect(() => {
    const setupChannel = async () => {
      if (!currentConversation) return;
      await currentConversation.watch();
      setActiveChannel?.(currentConversation);
    };
    setupChannel();

    return () => {
      setActiveChannel?.(undefined);
    };
  }, [currentConversation]);

  const baseChannelFilters = useMemo(() => {
    return {
      type: CHANNEL_TYPE,
      team: client?.user?.teams?.[0],
    };
  }, [client]);

  useEffect(() => {
    if (!client) return undefined;

    const updateUnreadStatus = async () => {
      const unreadChannels = await client.queryChannels(
        {
          ...baseChannelFilters,
          // @ts-ignore
          has_unread: true,
        },
        {},
        { limit: 1 }
      );

      setHasUnreadMessages(unreadChannels?.length > 0 || false);
    };

    // Find unread count for the user
    // @ts-ignore - Stream Chat types don't include total_unread_count yet
    if (client?.user?.total_unread_count) {
      updateUnreadStatus();
    }

    const fetchConversations = async () => {
      const channelResponse = await client.queryChannels(
        {
          ...baseChannelFilters,
          members: { $in: [client.user?.id as string] },
        },
        [{ last_message_at: -1 }]
      );

      const filteredConversations = channelResponse.filter((channel) =>
        conversationFilter(channel, baseChannelFilters, client)
      );

      setConversations(filteredConversations);
    };

    const handleMessageEvents = async () => {
      await updateUnreadStatus();

      if (conversations.length === 0) {
        await fetchConversations();
      }
    };

    fetchConversations();

    client.on('message.new', handleMessageEvents);
    client.on('notification.message_new', handleMessageEvents);
    client.on('message.read', updateUnreadStatus);
    client.on('notification.added_to_channel', fetchConversations);

    return () => {
      client.off('message.new', handleMessageEvents);
      client.off('notification.message_new', handleMessageEvents);
      client.off('message.read', updateUnreadStatus);
      client.off('notification.added_to_channel', fetchConversations);
    };
  }, [client]);

  return (
    <>
      {children({
        session: client,
        setActiveConversation: setActiveChannel,
        currentConversation,
        hasUnreadMessages,
        baseChannelFilters,
        conversations,
      })}
    </>
  );
};

export const StreamSession = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useCommunityContext();
  const { t } = useTranslation();
  const [createConversationAuth, { error: authError, loading: authLoading }] = useMutation(
    CREATE_CONVERSATION_AUTH,
    {
      context: { clientName: UNIFIED_GRAPH },
    }
  );
  const { uploadChatFile } = useFileOperations();

  const getTokenProvider = useCallback(async () => {
    const { data } = await createConversationAuth();
    if (!data?.createConversationAuth?.auth?.token) {
      throw new Error('Something went wrong.');
    }
    return data.createConversationAuth.auth.token;
  }, [createConversationAuth]);

  const client = useCreateChatClient({
    apiKey: process.env.STREAM_CHAT_API_KEY || '',
    tokenOrProvider: getTokenProvider,
    userData: {
      id: currentUser?.gid,
      name: currentUser?.fullName,
      image: currentUser?.avatarUrl,
    },
  });

  const maxFileSizeBytes = useMemo(() => MAX_FILE_SIZE_MB_IN_DMS * 1024 * 1024, []);

  const handleFileUpload = useCallback(
    async (file: File) => {
      try {
        return await uploadChatFile(file, {
          type: 'direct-messages',
        });
      } catch (error) {
        failureToastMessage(t('components-chat-file-upload-failed', 'File upload failed'));
        throw error;
      }
    },
    [uploadChatFile]
  );

  // Configure file uploads for GetStream chat
  useEffect(() => {
    if (!client) return;

    client.setMessageComposerSetupFunction(({ composer }) => {
      composer.updateConfig({
        attachments: {
          maxNumberOfFilesPerMessage: MAX_FILES_ALLOWED_IN_DMS,
          acceptedFiles: ACCEPTED_FILE_TYPES_IN_DMS,
          fileUploadFilter: (file: LocalUploadAttachment) => {
            const fileSize = Number(file.file_size);
            if (fileSize && fileSize > maxFileSizeBytes) {
              failureToastMessage(
                t(
                  'components-chat-file-too-large',
                  'File too large — maximum upload size is {{maxSize}}MB',
                  {
                    maxSize: MAX_FILE_SIZE_MB_IN_DMS,
                  }
                )
              );
              return false;
            }
            return true;
          },
          doUploadRequest: handleFileUpload,
        },
      });
    });
  }, [client, handleFileUpload, maxFileSizeBytes]);

  if (authLoading) {
    return <PageSkeleton />;
  }

  if (authError) {
    return <>{children}</>;
  }

  if (!client) {
    return null;
  }

  return (
    <Chat
      client={client}
      customClasses={{
        channelList: 'str-chat__channel-list-custom',
        channel: 'str-chat__channel-custom',
      }}
    >
      {children}
    </Chat>
  );
};

export default function StreamSessionProvider({ children }) {
  return (
    <StreamSession>
      <StreamContextMapper>{children}</StreamContextMapper>
    </StreamSession>
  );
}
